import {
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type Accessor,
  type JSX,
} from "solid-js";
import {
  Buildings,
  ClipboardText,
  DesktopTower,
  GearSix,
  HardDrives,
  List,
  MapTrifold,
  Moon,
  SignIn,
  SignOut,
  SquaresFour,
  Sun,
  UserCircleGear,
  WarningCircle,
} from "phosphor-solid";
import { createQuery } from "@tanstack/solid-query";
// Use native anchors here because router link context is not available in Root.
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Icon, type PhosphorIcon } from "@/components/ui/Icon";
import { authStore } from "@/auth/store";
import { signOut } from "@/auth";
import { useMe, hasPermission } from "@/lib/me";
import { canonicalPathForNav, handleInternalAnchorClick } from "@/lib/navigation";
import { theme, toggleTheme } from "@/lib/themeService";
import { formatError } from "@/lib/errors";
import { DashboardMapApi } from "@/lib/dashboard-map-api";
import { OperationsApi } from "@/lib/operations-api";
import { workspacePageMeta } from "@/lib/workspace-page";
import { getWorkspaceRouteInfo, workspaceDocumentTitle } from "@/lib/workspace-routes";

// Module-level signal — follows the same pattern as themeService.ts.
// Persists the user's sidebar preference across navigations.
const [sidebarCollapsed, setSidebarCollapsed] = createSignal(
  typeof localStorage !== "undefined" && localStorage.getItem("sidebar_collapsed") === "true",
);

function toggleSidebar() {
  const next = !sidebarCollapsed();
  setSidebarCollapsed(next);
  localStorage.setItem("sidebar_collapsed", String(next));
}

export { sidebarCollapsed };

export function AppShell(props: { children?: JSX.Element }) {
  const me = useMe();
  const [pathname, setPathname] = createSignal(window.location.pathname);
  const syncPathname = () => setPathname(window.location.pathname);
  window.addEventListener("popstate", syncPathname);
  onCleanup(() => window.removeEventListener("popstate", syncPathname));

  // True only on the map route — triggers full-bleed (h-screen, no container, no footer).
  const isFullBleed = createMemo(
    () => pathname() === "/dashboard/map" || pathname() === "/app/dashboard/map",
  );
  const routeInfo = createMemo(() => getWorkspaceRouteInfo(pathname()));
  const pageInfo = createMemo(() => {
    const override = workspacePageMeta();
    if (override?.pathname === pathname()) {
      return {
        ...routeInfo(),
        ...override,
        breadcrumbs: override.breadcrumbs ?? routeInfo().breadcrumbs,
      };
    }
    return routeInfo();
  });

  createEffect(() => {
    document.title = workspaceDocumentTitle(pageInfo());
  });

  const attention = createQuery(() => {
    const tenantId = me.status === "success" ? me.data?.tenant.tenantId : undefined;
    return {
      queryKey: ["workspace", "attention", tenantId ?? ""],
      queryFn: () => DashboardMapApi.dashboard.getSummary(tenantId!),
      enabled: Boolean(authStore.isAuthenticated() && tenantId),
      staleTime: 15_000,
      refetchInterval: 30_000,
    };
  });
  const activeAlerts = createQuery(() => {
    const tenantId = me.status === "success" ? me.data?.tenant.tenantId : undefined;
    return {
      queryKey: ["workspace", "active-alerts", tenantId ?? ""],
      queryFn: () => OperationsApi.alerts.list(tenantId!, { status: "Active", limit: 200 }),
      enabled: Boolean(authStore.isAuthenticated() && tenantId),
      staleTime: 15_000,
      refetchInterval: 30_000,
    };
  });
  const integrationHealth = createQuery(() => {
    const tenantId = me.status === "success" ? me.data?.tenant.tenantId : undefined;
    return {
      queryKey: ["workspace", "integration-health", tenantId ?? ""],
      queryFn: () => DashboardMapApi.dashboard.getIntegrationHealth(tenantId!),
      enabled: Boolean(authStore.isAuthenticated() && tenantId),
      staleTime: 30_000,
      refetchInterval: 60_000,
    };
  });
  const integrationAttentionCount = createMemo(
    () => (integrationHealth.data?.degraded ?? 0) + (integrationHealth.data?.failing ?? 0),
  );

  const handleThemeToggle = () => {
    toggleTheme();
  };

  return (
    <div
      classList={{
        "bg-background text-foreground": true,
        // Full-bleed map: fixed viewport; inner panes handle scroll.
        "h-screen overflow-hidden lg:flex lg:min-h-0": isFullBleed(),
        // Default: document scroll + sticky sidebar (avoids nested overflow flex bugs).
        "min-h-screen lg:flex lg:items-start": !isFullBleed(),
      }}
      onClick={handleInternalAnchorClick}
    >
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-popover focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-popover-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <Show
        when={authStore.isAuthenticated()}
        fallback={
          <UnauthenticatedShell onThemeToggle={handleThemeToggle}>
            {props.children}
          </UnauthenticatedShell>
        }
      >
        <aside
          classList={{
            "flex w-full shrink-0 flex-col border-b border-border bg-card/60 lg:border-b-0 lg:border-r transition-[width] duration-200": true,
            "lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto": !isFullBleed(),
            "lg:h-screen lg:overflow-y-auto": isFullBleed(),
            "lg:w-64": !sidebarCollapsed(),
            "lg:w-16": sidebarCollapsed(),
          }}
        >
          {/* Branding + collapse toggle */}
          <div class="flex items-center justify-between border-b border-border p-4 lg:p-3">
            <Show when={!sidebarCollapsed()}>
              <a href="/app" class="block font-semibold tracking-tight">
                Conatus
              </a>
            </Show>
            <Button
              variant="ghost"
              size="icon"
              class="hidden lg:flex shrink-0"
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed() ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed() ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Icon as={List} />
            </Button>
            {/* Mobile: always show wordmark */}
            <Show when={sidebarCollapsed()}>
              <span class="lg:hidden block font-semibold tracking-tight">Conatus</span>
            </Show>
          </div>

          <Switch>
            <Match when={me.status === "pending"}>
              <Show when={!sidebarCollapsed()} fallback={<div class="p-3" />}>
                <p class="p-4 text-sm text-muted-foreground">Loading navigation...</p>
              </Show>
            </Match>
            <Match when={me.status === "error"}>
              <div class="space-y-3 p-3 text-sm">
                <Show when={!sidebarCollapsed()}>
                  <p class="font-medium text-destructive">Could not load navigation.</p>
                  <p class="text-xs text-muted-foreground">{formatError(me.error)}</p>
                </Show>
                <Button variant="outline" size="sm" onClick={() => void signOut()}>
                  <Icon as={SignOut} />
                  <Show when={!sidebarCollapsed()}>Sign out</Show>
                </Button>
              </div>
            </Match>
            <Match when={me.status === "success" && me.data}>
              {(data) => (
                <>
                  <nav
                    classList={{
                      "flex-1 p-3 text-sm": true,
                      "space-y-5": !sidebarCollapsed(),
                      "space-y-2": sidebarCollapsed(),
                    }}
                  >
                    <NavSection title="Monitor">
                      <Show when={hasPermission(data(), "sites:list")}>
                        <ShellLink href="/dashboard" icon={SquaresFour} pathname={pathname}>
                          Dashboard
                        </ShellLink>
                      </Show>
                      <Show when={hasPermission(data(), "sites:list")}>
                        <ShellLink href="/dashboard/map" icon={MapTrifold} pathname={pathname}>
                          Map
                        </ShellLink>
                      </Show>
                    </NavSection>

                    <NavSection title="Triage">
                      <Show when={hasPermission(data(), "alerts:list")}>
                        <ShellLink
                          href="/alerts"
                          icon={WarningCircle}
                          pathname={pathname}
                          badge={activeAlerts.isSuccess ? activeAlerts.data.items.length : undefined}
                        >
                          Alerts
                        </ShellLink>
                      </Show>
                      <Show when={hasPermission(data(), "work-items:list")}>
                        <ShellLink
                          href="/work"
                          icon={ClipboardText}
                          pathname={pathname}
                          badge={attention.data?.cards.openWorkItems}
                        >
                          Work
                        </ShellLink>
                      </Show>
                    </NavSection>

                    <NavSection title="Assets">
                      <Show when={hasPermission(data(), "sites:list")}>
                        <ShellLink href="/registry" icon={SquaresFour} pathname={pathname}>
                          Registry
                        </ShellLink>
                      </Show>
                      <Show when={hasPermission(data(), "sites:list")}>
                        <ShellLink href="/registry/sites" icon={Buildings} pathname={pathname}>
                          Sites
                        </ShellLink>
                      </Show>
                      <Show when={hasPermission(data(), "assets:list")}>
                        <ShellLink href="/registry/assets" icon={HardDrives} pathname={pathname}>
                          Assets
                        </ShellLink>
                      </Show>
                      <Show when={hasPermission(data(), "devices:list")}>
                        <ShellLink href="/registry/devices" icon={DesktopTower} pathname={pathname}>
                          Devices
                        </ShellLink>
                      </Show>
                    </NavSection>

                    <Show when={hasPermission(data(), "integrations:read")}>
                      <NavSection title="Setup">
                        <ShellLink
                          href="/integrations"
                          icon={GearSix}
                          pathname={pathname}
                          badge={integrationAttentionCount()}
                        >
                          Integrations
                        </ShellLink>
                      </NavSection>
                    </Show>

                    <Show when={hasPermission(data(), "users:read")}>
                      <NavSection title="Admin">
                        <ShellLink href="/app/admin" icon={GearSix} pathname={pathname}>
                          Overview
                        </ShellLink>
                        <Show when={hasPermission(data(), "tenants:list")}>
                          <ShellLink href="/app/admin/tenants" icon={Buildings} pathname={pathname}>
                            Tenants
                          </ShellLink>
                        </Show>
                        <Show when={data().tenant.tenantId}>
                          <ShellLink
                            href={`/app/admin/tenants/${data().tenant.tenantId}`}
                            icon={UserCircleGear}
                            pathname={pathname}
                          >
                            My tenant
                          </ShellLink>
                        </Show>
                        <ShellLink href="/app/admin/api-probe" icon={HardDrives} pathname={pathname}>
                          API Probe
                        </ShellLink>
                      </NavSection>
                    </Show>
                  </nav>

                  <div
                    classList={{
                      "border-t border-border p-3": true,
                      "space-y-3": !sidebarCollapsed(),
                      "space-y-2": sidebarCollapsed(),
                    }}
                  >
                    <Show when={!sidebarCollapsed()}>
                      <UserBadge me={me} />
                      <Button
                        variant="outline"
                        size="sm"
                        class="w-full justify-start"
                        onClick={() => (window.location.href = "/app/account/security")}
                      >
                        <Icon as={GearSix} />
                        Account security
                      </Button>
                    </Show>
                    <Show
                      when={!sidebarCollapsed()}
                      fallback={
                        <div class="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleThemeToggle}
                            aria-label={
                              theme() === "dark" ? "Switch to light mode" : "Switch to dark mode"
                            }
                            title={
                              theme() === "dark" ? "Switch to light mode" : "Switch to dark mode"
                            }
                          >
                            <Icon as={theme() === "dark" ? Sun : Moon} />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => void signOut()}
                            aria-label="Sign out"
                            title="Sign out"
                          >
                            <Icon as={SignOut} />
                          </Button>
                        </div>
                      }
                    >
                      <div class="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleThemeToggle}
                          aria-label={
                            theme() === "dark" ? "Switch to light mode" : "Switch to dark mode"
                          }
                          title={
                            theme() === "dark" ? "Switch to light mode" : "Switch to dark mode"
                          }
                        >
                          <Icon as={theme() === "dark" ? Sun : Moon} />
                          {theme() === "dark" ? "Light" : "Dark"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => void signOut()}>
                          <Icon as={SignOut} />
                          Sign out
                        </Button>
                      </div>
                    </Show>
                  </div>
                </>
              )}
            </Match>
          </Switch>
        </aside>

        <div
          classList={{
            "flex min-w-0 flex-1 flex-col": true,
            "min-h-0 overflow-hidden": isFullBleed(),
          }}
        >
          <main
            id="main-content"
            tabIndex={-1}
            classList={{
              "flex-1 flex flex-col": true,
              "min-h-0": isFullBleed(),
            }}
          >
            <div
              classList={{
                "flex-1 flex flex-col min-h-0": isFullBleed(),
                "container py-6 lg:py-8": !isFullBleed(),
              }}
            >
              {props.children}
            </div>
          </main>

          <Show when={!isFullBleed()}>
            <footer class="shrink-0 border-t border-border bg-card/25">
              <div class="container py-4 text-xs text-muted-foreground">
                Conatus &middot; operational inventory
              </div>
            </footer>
          </Show>
        </div>
      </Show>
    </div>
  );
}

function UnauthenticatedShell(props: { children?: JSX.Element; onThemeToggle: () => void }) {
  return (
    <div class="flex min-h-screen w-full flex-col">
      <header class="border-b border-border bg-card/35">
        <div class="container flex h-14 items-center justify-between gap-4">
          <div>
            <a href="/" class="font-semibold tracking-tight">
              Conatus
            </a>
            <p class="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              asset monitoring (dev)
            </p>
          </div>
          <nav class="flex items-center gap-3 text-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={props.onThemeToggle}
              aria-label={theme() === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme() === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              <Icon as={theme() === "dark" ? Sun : Moon} />
              {theme() === "dark" ? "Light" : "Dark"}
            </Button>
            <a
              href="/auth/login"
              class="inline-flex items-center gap-1.5 text-muted-foreground transition-colors duration-150 ease-control hover:text-foreground"
            >
              <Icon as={SignIn} />
              Sign in
            </a>
          </nav>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} class="flex-1">
        <div class="container py-8">{props.children}</div>
      </main>
    </div>
  );
}

function NavSection(props: { title: string; children: JSX.Element }) {
  return (
    <section class="space-y-1">
      <Show when={!sidebarCollapsed()}>
        <h2 class="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {props.title}
        </h2>
      </Show>
      <div
        classList={{
          "space-y-1": true,
          "flex flex-col items-center": sidebarCollapsed(),
        }}
      >
        {props.children}
      </div>
    </section>
  );
}

function ShellLink(props: {
  href: string;
  icon: PhosphorIcon;
  /** Reactive pathname — required so active state updates on SPA navigations (window.location is not tracked). */
  pathname: Accessor<string>;
  badge?: number;
  children: JSX.Element;
}) {
  const isActive = createMemo(() => {
    const path = canonicalPathForNav(props.pathname());
    if (props.href === "/dashboard" || props.href === "/registry" || props.href === "/app/admin") {
      return path === props.href;
    }
    return path === props.href || path.startsWith(`${props.href}/`);
  });

  return (
    <a
      href={props.href}
      aria-current={isActive() ? "page" : undefined}
      title={sidebarCollapsed() ? String(props.children) : undefined}
      classList={{
        "flex items-center rounded-md py-1.5 transition-colors duration-150 ease-control": true,
        "gap-2 px-2": !sidebarCollapsed(),
        "justify-center px-0 w-10": sidebarCollapsed(),
        "bg-secondary text-secondary-foreground font-medium": isActive(),
        "text-muted-foreground hover:text-foreground": !isActive(),
      }}
    >
      <Icon as={props.icon} />
      <Show when={!sidebarCollapsed()}>
        <span class="min-w-0 flex-1 truncate">{props.children}</span>
        <Show when={typeof props.badge === "number" && props.badge > 0}>
          <Badge variant="warning" class="ml-auto">
            {props.badge}
          </Badge>
        </Show>
      </Show>
    </a>
  );
}

function UserBadge(props: { me: ReturnType<typeof useMe> }) {
  return (
    <Show
      when={props.me.status === "success" && props.me.data}
      fallback={<span class="text-xs text-muted-foreground">Loading…</span>}
    >
      {(data) => {
        const primaryRole = data().isPlatformAdmin
          ? "PlatformAdmin"
          : (data().roles.find((r) => r.status === "Active")?.roleKey ?? "Member");
        return (
          <div class="flex flex-col leading-tight">
            <span class="text-xs font-medium text-foreground">
              {data().user.displayName ?? data().user.email}
            </span>
            <span class="text-[10px] text-muted-foreground">
              {data().tenant.displayName} · {primaryRole}
            </span>
          </div>
        );
      }}
    </Show>
  );
}
